/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import axios from "axios";
import { APIGateways } from "../../../utility/APIGateways";
import { getAuthHeader } from "../../../utility/auth";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import { isEmptyObject, redactPII } from "../../../utility/helpers";
import { PII_FIELDS } from "../../../utility/constants";

const COMPONENT_NAME = "Admin settingsManager/services/ExternalContactsService";

/**
 * Creates or updates a list of contacts
 */
export const updateExternalContacts = async (externalContacts) => {
	try {
		let response = await axios.put(`${APIGateways.externalContactsEndpoint}`, externalContacts, {
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error updating external contacts`, {
			additionalData: {
				error: errorToObject(error),
				externalContacts: redactPII(externalContacts, PII_FIELDS.externalContact),
			},
			logCode: errorCodes.UPDATE_EXT_CONTACTS_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

/**
 * Gets all external contacts
 */
export const getExternalContacts = async () => {
	try {
		let response = await axios.get(`${APIGateways.externalContactsEndpoint}`, {
			headers: await getAuthHeader(),
		});
		return response.data;
	} catch (error) {
		v2.error(`Error getting all external contacts`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.GET_EXT_CONTACTS_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

/**
 * Delete external contacts by ids
 */
export const deleteExternalContacts = async (externalContactIds) => {
	try {
		let response = await axios.delete(`${APIGateways.externalContactsEndpoint}`, {
			data: externalContactIds,
			headers: await getAuthHeader(),
		});
		return response.data.result;
	} catch (error) {
		v2.error(`Error deleting external contacts`, {
			additionalData: {
				error: errorToObject(error),
				externalContactIds,
			},
			logCode: errorCodes.DELETE_EXT_CONTACTS_API_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

const paginateRequest = async (url, client, headers, data, pageSize = 999) => {
	let nextLink;
	let combinedResult = [];
	do {
		const queryString = nextLink?.split("?")?.[1] || `$top=${pageSize}`; // nextLink = "https://graph.microsoft.com/v1.0/groups/a2d7f79d-a9ba-4f14-959e-40d7680aae9f/members?$top=5&$skiptoken=RFNwdAoAAQAAA...AAAAA"
		const response = await client(`${url}?${queryString}`, headers, data);
		if (isEmptyObject(response.data)) {
			throw new Error(
				"Failed to get the Azure Active Directory users. Please check the Azure configuration and try again.",
			);
		}
		combinedResult = [...combinedResult, ...response.data.value];
		nextLink = response.data["@odata.nextLink"];
	} while (!!nextLink);
	return combinedResult;
};

export const getAdAllGroups = async () => {
	try {
		let groups = await paginateRequest(`${APIGateways.azureAdEndpoint}/groups`, axios.get, {
			headers: await getAuthHeader(),
		});
		return groups;
	} catch (error) {
		v2.error(`Error while getting Azure Groups`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.AZURE_GET_GROUPS_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};

export const getAdGroupMembers = async (groupId) => {
	try {
		let members = await paginateRequest(`${APIGateways.azureAdEndpoint}/group/${groupId}`, axios.get, {
			headers: await getAuthHeader(),
		});
		return members;
	} catch (error) {
		v2.error(`Error while getting Azure Groups Members`, {
			additionalData: {
				error: errorToObject(error),
			},
			logCode: errorCodes.AZURE_GET_GROUPS_ERROR,
			componentName: COMPONENT_NAME,
		});
		throw error;
	}
};
